// src/services/camunda.service.js
import { http } from "./http";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------
// Helper para reintentar en caso de OptimisticLockingException
// ---------------------------------------------------------

async function withOptimisticRetry(fn, label, retries = 1, delayMs = 150) {
  try {
    return await fn();
  } catch (err) {
    const isAxios = axios.isAxiosError?.(err) || err?.isAxiosError;

    const status = err?.response?.status;
    const data = err?.response?.data;

    let bodyText = "";
    if (typeof data === "string") {
      bodyText = data;
    } else if (data && typeof data === "object") {
      if (typeof data.message === "string") {
        bodyText = data.message;
      } else {
        try {
          bodyText = JSON.stringify(data);
        } catch {
          bodyText = "";
        }
      }
    }

    const isOptimistic =
      isAxios &&
      status === 500 &&
      bodyText.includes("OptimisticLockingException");

    if (isOptimistic && retries > 0) {
      console.warn(
        `[Camunda] ${label}: OptimisticLockingException, retrying... remaining retries=${retries}`
      );
      await new Promise((res) => setTimeout(res, delayMs));
      return withOptimisticRetry(fn, label, retries - 1, delayMs);
    }

    throw err;
  }
}

/* ============================================================
   1. Obtain all variables of a process instance
   GET /variable-instance?processInstanceIdIn={id}
   ============================================================ */
export async function getCamundaVariables(processInstanceId) {
  if (!processInstanceId) {
    throw new Error("getCamundaVariables: 'processInstanceId' is required");
  }

  const { data } = await http.get(`${API_URL}/engine-rest/variable-instance`, {
    params: { processInstanceIdIn: processInstanceId },
  });

  return data; // returns array
}

/* ============================================================
   1b. Obtain ALL VARIABLES visible for a TASK (admin, assignee, etc.)
   GET /task/{taskId}/variables
   ============================================================ */
export async function getTaskVariables(
  taskId,
  { deserializeValues = false } = {}
) {
  if (!taskId) {
    throw new Error("getTaskVariables: 'taskId' is required");
  }

  const { data } = await http.get(
    `${API_URL}/engine-rest/task/${taskId}/variables`,
    {
      params: { deserializeValues },
    }
  );

  // data es un objeto { varName: { type, value, ... }, ... }
  return data;
}

/* ============================================================
   2. Obtain ALL TASKS for a process instance
   GET /task?processInstanceId={id}
   ============================================================ */
export async function getTasksByProcessInstance(processInstanceId) {
  if (!processInstanceId) {
    throw new Error(
      "getTasksByProcessInstance: 'processInstanceId' is required"
    );
  }

  return withOptimisticRetry(
    async () => {
      const { data } = await http.get(`${API_URL}/engine-rest/task`, {
        params: { processInstanceId },
      });
      return data; // array of tasks
    },
    "getTasksByProcessInstance"
  );
}

/* ============================================================
   2b. Obtain TASKS by assignee
   GET /task?assignee={userName}
   ============================================================ */
export async function getTasksByAssignee(assignee) {
  if (!assignee) {
    throw new Error("getTasksByAssignee: 'assignee' is required");
  }

  return withOptimisticRetry(
    async () => {
      const { data } = await http.get(`${API_URL}/engine-rest/task`, {
        params: { assignee },
      });
      return data; // array de tasks
    },
    "getTasksByAssignee"
  );
}

/* ============================================================
   3. LOCAL VARIABLES of a task
   ============================================================ */

export async function getTaskLocalVariables(
  taskId,
  { deserializeValues = false } = {},
  requestOptions = {}
) {
  if (!taskId) {
    throw new Error("getTaskLocalVariables: 'taskId' is required");
  }

  const { data } = await http.get(
    `${API_URL}/api/assignments/task/${encodeURIComponent(taskId)}/localVariables`,
    { ...requestOptions, params: { deserializeValues } }
  );

  return data; // object: { varName: { type, value, ... } }
}

export async function getTaskLocalVariable(taskId, varName) {
  if (!taskId) {
    throw new Error("getTaskLocalVariable: 'taskId' is required");
  }
  if (!varName) {
    throw new Error("getTaskLocalVariable: 'varName' is required");
  }

  const { data } = await http.get(
    `${API_URL}/engine-rest/task/${taskId}/localVariables/${varName}`
  );

  return data; // single variable object
}

/* ============================================================
   4. CREATE or UPDATE a SINGLE local variable on a task
   ============================================================ */
export async function setTaskLocalVariable(taskId, varName, value, type) {
  if (!taskId) throw new Error("setTaskLocalVariable: 'taskId' is required");
  if (!varName) throw new Error("setTaskLocalVariable: 'varName' is required");

  let camundaType = type;
  if (!camundaType) {
    switch (typeof value) {
      case "string":
        camundaType = "String";
        break;
      case "number":
        camundaType = Number.isInteger(value) ? "Integer" : "Double";
        break;
      case "boolean":
        camundaType = "Boolean";
        break;
      case "object":
        camundaType = "Json";
        break;
      default:
        camundaType = "String";
    }
  }

  const payload = {
    value,
    type: camundaType,
  };

  const { data } = await http.put(
    `${API_URL}/engine-rest/task/${taskId}/localVariables/${varName}`,
    payload
  );

  return data;
}

/* ============================================================
   4b. CREATE or UPDATE VARIAS local variables en una sola llamada
   ============================================================ */
export async function setTaskLocalVariables(taskId, vars, requestOptions = {}) {
  if (!taskId) throw new Error("setTaskLocalVariables: 'taskId' is required");
  if (!vars || typeof vars !== "object") {
    throw new Error("setTaskLocalVariables: 'vars' object is required");
  }

  const modifications = {};

  Object.entries(vars).forEach(([name, def]) => {
    if (!def || typeof def !== "object" || !("value" in def)) {
      throw new Error(
        `setTaskLocalVariables: invalid definition for variable '${name}'`
      );
    }

    let { value, type } = def;

    let camundaType = type;
    if (!camundaType) {
      switch (typeof value) {
        case "string":
          camundaType = "String";
          break;
        case "number":
          camundaType = Number.isInteger(value) ? "Integer" : "Double";
          break;
        case "boolean":
          camundaType = "Boolean";
          break;
        case "object":
          camundaType = "Json";
          break;
        default:
          camundaType = "String";
      }
    }

    modifications[name] = {
      value,
      type: camundaType,
    };
  });

  const payload = {
    modifications,
    deletions: [],
  };

  const { data } = await http.post(
    `${API_URL}/api/assignments/task/${encodeURIComponent(taskId)}/localVariables`,
    payload,
    requestOptions
  );

  return data;
}

/* ============================================================
   5. Obtain ALL HISTORIC / ENDED TASKS for a process instance
   ============================================================ */
export async function getHistoricTasksByProcessInstance(processInstanceId) {
  if (!processInstanceId) {
    throw new Error(
      "getHistoricTasksByProcessInstance: 'processInstanceId' is required"
    );
  }

  return withOptimisticRetry(
    async () => {
      const { data } = await http.get(
        `${API_URL}/engine-rest/history/task`,
        {
          params: { processInstanceId },
        }
      );
      return data; // array of historic tasks
    },
    "getHistoricTasksByProcessInstance"
  );
}

/* ============================================================
   6. COMPLETE TASK
   ============================================================ */
export async function completeTask(taskId, vars = {}) {
  if (!taskId) {
    throw new Error("completeTask: 'taskId' is required");
  }

  if (vars === null || typeof vars !== "object") {
    throw new Error("completeTask: 'vars' must be an object (or omitted)");
  }

  const payload = {
    variables: vars,
  };

  const { data } = await http.post(
    `${API_URL}/api/assignments/task/${encodeURIComponent(taskId)}/complete`,
    payload
  );

  return data;
}

async function assignmentTiming(taskId, start) {
  const requested = Date.now();
  const url = `${API_URL}/api/assignments/task/${encodeURIComponent(taskId)}/${start ? "start" : "timing"}`;
  const { data } = start ? await http.post(url, {}, { silentError: true }) : await http.get(url, { silentError: true });
  if (!data || !data.serverNow || !Number.isFinite(Date.parse(data.serverNow))) throw new Error("No se pudo consultar el temporizador.");
  return { ...data, clockOffset: Date.parse(data.serverNow) - (requested + Date.now()) / 2 };
}

export const getAssignmentTiming = taskId => assignmentTiming(taskId, false);
export const startAssignment = taskId => assignmentTiming(taskId, true);

export async function submitDeadlineSnapshot(taskId, draft) {
  const { data } = await http.post(`${API_URL}/api/assignments/task/${encodeURIComponent(taskId)}/deadline-snapshot`,
    { type: "String", value: JSON.stringify(draft) }, { silentError: true });
  return data;
}

/* ============================================================
   7. HISTORIC TASKS by assignee
   ============================================================ */
export async function getHistoricTasksByAssignee(assignee) {
  if (!assignee) {
    throw new Error("getHistoricTasksByAssignee: 'assignee' is required");
  }

  return withOptimisticRetry(
    async () => {
      const { data } = await http.get(
        `${API_URL}/engine-rest/history/task`,
        {
          params: {
            taskAssignee: assignee,
            finished: true,
            sortBy: "endTime",
            sortOrder: "desc",
          },
        }
      );

      return data || [];
    },
    "getHistoricTasksByAssignee"
  );
}

export async function getHistoricProcessInstance(processInstanceId) {
  if (!processInstanceId) {
    throw new Error(
      "getHistoricProcessInstance: 'processInstanceId' is required"
    );
  }

  const { data } = await http.get(
    `${API_URL}/engine-rest/history/process-instance/${processInstanceId}`,
    {
      params: {
        includeProcessVariables: true,
      },
    }
  );

  return data;
}

export async function getHistoricVariablesByProcessInstance(processInstanceId) {
  const { data } = await http.get(
    `${API_URL}/engine-rest/history/variable-instance`,
    {
      params: {
        processInstanceIdIn: processInstanceId,
      },
    }
  );

  return data.map((v) => ({
    name: v.name,
    type: v.type,
    value: v.value,
  }));
}

/* ============================================================
   7b. HISTORIC VARIABLES BY TASK (admin, assignee, etc.)
   GET /history/variable-instance?taskIdIn={taskId}
   ============================================================ */
export async function getHistoricVariablesByTask(taskId) {
  if (!taskId) {
    throw new Error("getHistoricVariablesByTask: 'taskId' is required");
  }

  const { data } = await http.get(
    `${API_URL}/engine-rest/history/variable-instance`,
    {
      params: {
        taskIdIn: taskId,
      },
    }
  );

  // Normalizamos igual que en getHistoricVariablesByProcessInstance
  return data.map((v) => ({
    name: v.name,
    type: v.type,
    value: v.value,
  }));
}

/* ============================================================
   8. User profile
   ============================================================ */
export async function getUserProfile(userId) {
  if (!userId) {
    throw new Error("getUserProfile: 'userId' is required");
  }

  const { data } = await http.get(
    `${API_URL}/engine-rest/user/${encodeURIComponent(userId)}/profile`
  );

  return data;
}
